import React, { useEffect, useRef, useState } from 'react';
import { useSession } from './context/SessionContext';
import { useTheme } from './theme/ThemeProvider';
import { SignInScreen } from './screens/SignInScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { AssistantScreen } from './screens/AssistantScreen';
import {
  OziraContextBar,
  OziraProductSwitcher,
  OziraTrustRail,
  OziraMark,
  buildProductHandoffUrl,
} from 'ozira-shell-kit';
import { getEnvironment, getConfig } from './env';
import {
  CodeWorkspace,
  ForwardWorkspace,
  MobilePaneToolbar,
  MobileSheetBackdrop,
  PWorkspace,
  ProductSidebarShell,
  RecentFilesSection,
  useMobilePane,
} from './product-shell/ProductWorkspaces';
import { getDesignProjects, type DesignProjectSummary } from './lib/conversationsApi';
import './product-shell/productShell.css';
import { isSettingsRoute, settingsPath } from './settingsRoute';
import { isShareRoute, shareToken } from './shareRoute';
import { SharedConversationScreen } from './screens/SharedConversationScreen';
import { reloadWorkspace } from './lib/workspaceLocation';
import './sessionRestore.css';

function WorkspaceRestoreState({ failed = false }: { failed?: boolean }) {
  return <div className="ozira-session-restore" aria-busy={!failed}>
    <aside aria-hidden="true"><div className="ozira-session-restore__brand"><OziraMark size={36} /><strong>OZIRA</strong></div></aside>
    <main>
      <div role={failed ? 'alert' : 'status'}>
        {failed ? 'Could not restore your session. Please try again.' : 'Restoring your workspace…'}
        {failed && <button type="button" onClick={reloadWorkspace}>Try again</button>}
      </div>
      <div className="ozira-session-restore__composer" aria-hidden="true" />
    </main>
  </div>;
}

export type ProductId = 'assistant' | 'code' | 'design' | 'p' | 'forward';

const PRODUCT_META: Record<ProductId, {
  description: string;
  eyebrow: string;
  figma: string;
  label: string;
  title: string;
}> = {
  assistant: {
    description: 'Keep projects, sources, and approvals close while you work with OZIRA.',
    eyebrow: 'OZIRA ASSISTANT',
    figma: '66:171',
    label: 'Assistant',
    title: 'Think, create, and act in one conversation.',
  },
  code: {
    description: 'Review code, collaborate with the agent, and keep execution evidence close.',
    eyebrow: 'OZIRA CODE',
    figma: '66:184',
    label: 'Code',
    title: 'Build with your repository in view.',
  },
  design: {
    description: 'Shape ideas with pages, assets, layers, properties, and export controls.',
    eyebrow: 'OZIRA DESIGN',
    figma: '66:197',
    label: 'Design',
    title: 'Create on a flexible canvas.',
  },
  p: {
    description: 'Follow sources, signals, and provenance from question to conclusion.',
    eyebrow: 'OZIRA COWORK',
    figma: '66:210',
    label: 'Cowork',
    title: 'Investigate evidence with clarity.',
  },
  forward: {
    description: 'Organize tasks, approvals, schedules, and run history in one workflow.',
    eyebrow: 'OZIRA FORWARD',
    figma: '66:223',
    label: 'Forward',
    title: 'Turn plans into coordinated work.',
  },
};

// The URL segment each product lands on when switched to in place. Kept
// separate from the internal ProductId ('assistant') because the canonical
// public path for that product is /chat, not /assistant -- the internal id
// stays put to avoid renaming it through settingsPath()/sharePath() and
// every other place that already spells it 'assistant'.
const PRODUCT_PATH_SEGMENT: Record<ProductId, string> = {
  assistant: 'chat',
  code: 'code',
  design: 'design',
  p: 'cowork',
  forward: 'forward',
};

// Settings keeps each product's already-stable internal id as its own path
// segment (settingsPath()'s existing /assistant/settings default, not the
// 'chat'/'cowork' aliases above) so existing /assistant/settings deep links
// and the no-arg settingsPath() call sites already in this codebase and its
// tests keep resolving exactly as before.
const SETTINGS_PRODUCT_SEGMENT: Record<ProductId, string> = {
  assistant: 'assistant',
  code: 'code',
  design: 'design',
  p: 'p',
  forward: 'forward',
};

export function resolveProductId(locationLike: Pick<Location, 'hostname' | 'search' | 'pathname'>): ProductId {
  const hostname = locationLike.hostname.toLowerCase();
  const requested = new URLSearchParams(locationLike.search).get('product');
  const pathname = locationLike.pathname.replace(/\/+$/, '') || '/';
  const localOverrideAllowed = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
  if (localOverrideAllowed && requested && Object.prototype.hasOwnProperty.call(PRODUCT_META, requested)) {
    return requested as ProductId;
  }
  // /chat is the canonical path for the Assistant product; /assistant keeps
  // working as a legacy alias (settingsPath()/sharePath() still deep-link
  // through /assistant/, and existing bookmarks shouldn't break).
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return 'assistant';
  if (pathname === '/assistant' || pathname.startsWith('/assistant/')) return 'assistant';
  if (pathname === '/code' || pathname.startsWith('/code/')) return 'code';
  if (pathname === '/design' || pathname.startsWith('/design/')) return 'design';
  if (pathname === '/forward' || pathname.startsWith('/forward/')) return 'forward';
  // /cowork is the canonical path for the P product; /p keeps working as a
  // legacy alias, same pattern as /assistant above.
  if (pathname === '/cowork' || pathname.startsWith('/cowork/')) return 'p';
  if (pathname === '/p' || pathname.startsWith('/p/')) return 'p';
  const firstLabel = hostname.split('.')[0];
  if (firstLabel === 'ai' || firstLabel === 'app') return 'assistant';
  if (firstLabel === 'code' || firstLabel === 'design' || firstLabel === 'p' || firstLabel === 'forward') return firstLabel;
  return 'assistant';
}

function isPrimaryDomainEntry(locationLike: Pick<Location, 'hostname' | 'pathname'>): boolean {
  const pathname = locationLike.pathname.replace(/\/+$/, '') || '/';
  return locationLike.hostname.toLowerCase() === 'www.ozira.et' && (pathname === '/' || pathname === '/platform');
}

export function isSignInRequested(locationLike: Pick<Location, 'search'>): boolean {
  return new URLSearchParams(locationLike.search).get('signin') === '1';
}

// The crux of the redirect fix: on production every product URL resolves to
// this exact same origin (server.js's isPrimaryHost routing), so switching
// products there should update the URL in place rather than reload. On
// staging each product genuinely lives on its own subdomain, so a real
// navigation is the only way to reach it. Pulled out as a pure function so
// the decision itself -- not just its use inside a click handler -- is
// directly testable.
export function isSameOriginProductUrl(targetUrl: string, currentOrigin: string): boolean {
  try {
    return new URL(targetUrl, currentOrigin).origin === currentOrigin;
  } catch {
    return false;
  }
}

function AssistantWorkspace({ authenticated, onOpenSettings, onNavigateProduct }: {
  authenticated: boolean;
  loading: boolean;
  onOpenSettings: () => void;
  onNavigateProduct: (productId: ProductId) => void;
}) {
  const mobile = useMobilePane();
  const [activeSection, setActiveSection] = useState<'home' | 'assistant' | 'activity' | 'files'>('assistant');

  // No locked-preview skeleton with disabled placeholder buttons and a "Sign
  // in to start" modal trigger -- every Assistant entry point (not just the
  // primary domain) shows the real sign-in screen directly to an
  // unauthenticated visitor, same as everywhere else in the product.
  if (!authenticated) {
    return <SignInScreen />;
  }

  return (
    <div className="ozira-assistant-responsive">
      <MobileSheetBackdrop open={Boolean(mobile.pane)} onClose={mobile.closePane} />
      <div className="ozira-assistant-live">
        <div
          className="ozira-assistant-live__conversation"
          inert={mobile.pane === 'Context' ? '' : undefined}
          aria-hidden={mobile.pane === 'Context' ? true : undefined}
        >
          <AssistantScreen
            activeSection={activeSection}
            onNavigate={setActiveSection}
            mobileSidebarOpen={mobile.pane === 'Conversations'}
            onMobileSidebarClose={mobile.closePane}
            onMobileSidebarCloseWithoutRestore={mobile.closePaneWithoutRestore}
            onMobileSidebarOpen={() => mobile.openPane('Conversations')}
            onOpenSettings={onOpenSettings}
            onNavigateProduct={(id) => onNavigateProduct(id as ProductId)}
          />
        </div>
        <aside
          className={`ozira-shell-panel ozira-assistant-live__context ozira-product-secondary-pane${mobile.pane === 'Context' ? ' is-mobile-open' : ''}`}
          id="ozira-context-panel"
          aria-label={mobile.pane === 'Context' ? 'Context' : 'Assistant context'}
          role={mobile.pane === 'Context' ? 'dialog' : undefined}
          aria-modal={mobile.pane === 'Context' ? true : undefined}
        >
          <button type="button" className="ozira-mobile-sheet-close" onClick={mobile.closePane} aria-label="Close Context">Close</button>
          <h2>Context</h2>
          <p className="ozira-truth-note">No context available.</p>
        </aside>
      </div>
    </div>
  );
}

function DesignRecentSlot({ authenticated }: { authenticated: boolean }) {
  const [projects, setProjects] = useState<DesignProjectSummary[] | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    getDesignProjects().then((list) => { if (!cancelled) setProjects(list); }).catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, [authenticated]);

  const items = projects?.map((project) => ({
    id: project.id,
    label: project.name,
    meta: new Date(project.updatedAt).toLocaleDateString(),
  }));

  return (
    <RecentFilesSection
      title="Recent files"
      emptyMessage={authenticated ? 'No design projects yet.' : 'Sign in to see your design projects.'}
      items={items}
    />
  );
}

function DesignWorkspace({ authenticated, loading, onNavigateProduct, onOpenSettings, organizationTenantId }: { authenticated: boolean; loading: boolean; onNavigateProduct: (productId: ProductId) => void; onOpenSettings?: () => void; organizationTenantId: string | null }) {
  const mobile = useMobilePane();
  const [showEngine, setShowEngine] = useState(authenticated);

  useEffect(() => {
    if (authenticated) setShowEngine(true);
  }, [authenticated]);

  return (
    <ProductSidebarShell productId="design" organizationTenantId={organizationTenantId} onNavigateProduct={onNavigateProduct} onOpenSettings={onOpenSettings} recentSlot={<DesignRecentSlot authenticated={authenticated} />}>
    <section className="ozira-design-workspace" aria-label="OZIRA Design editor" data-figma-node="66:197">
      <MobilePaneToolbar labels={['Pages / Assets', 'Inspector']} activePane={mobile.pane} onOpen={mobile.openPane} />
      <MobileSheetBackdrop open={Boolean(mobile.pane)} onClose={mobile.closePane} />

      <aside
        className={`ozira-shell-panel ozira-product-secondary-pane${mobile.pane === 'Pages / Assets' ? ' is-mobile-open' : ''}`}
        id="ozira-pages-assets-panel"
        role={mobile.pane === 'Pages / Assets' ? 'dialog' : undefined}
        aria-modal={mobile.pane === 'Pages / Assets' ? true : undefined}
        aria-label="Pages and assets"
      >
        <button type="button" className="ozira-mobile-sheet-close" onClick={mobile.closePane} aria-label="Close Pages / Assets">Close</button>
        <h2>Pages / Assets</h2>
        <ul className="ozira-panel-list">
          {['Pages', 'Components', 'Images', 'Brand assets', 'History'].map((item) => (
            <li key={item}><button type="button" disabled>{item}</button></li>
          ))}
        </ul>
      </aside>

      <section
        className={`ozira-design-host ozira-product-main-panel${showEngine ? '' : ' ozira-design-host--preview'}`}
        aria-label="Canvas"
        inert={mobile.pane ? '' : undefined}
        aria-hidden={mobile.pane ? true : undefined}
      >
        {showEngine ? (
          <iframe
            className="ozira-design-host__frame"
            src="/design-app/engine.html?embedded=1"
            title="OZIRA Design canvas editor"
          />
        ) : (
          <>
            <h2>Canvas</h2>
            <p className="ozira-design-preview-copy">Create, edit, arrange, and prototype in one visual workspace.</p>
            <button
              type="button"
              className="ozira-design-preview-action"
              disabled={loading}
              onClick={() => setShowEngine(true)}
            >
              {loading ? 'Checking session…' : 'Sign in to create'}
            </button>
          </>
        )}
      </section>

      <aside
        className={`ozira-shell-panel ozira-product-secondary-pane${mobile.pane === 'Inspector' ? ' is-mobile-open' : ''}`}
        id="ozira-inspector-panel"
        role={mobile.pane === 'Inspector' ? 'dialog' : undefined}
        aria-modal={mobile.pane === 'Inspector' ? true : undefined}
        aria-label="Inspector"
      >
        <button type="button" className="ozira-mobile-sheet-close" onClick={mobile.closePane} aria-label="Close Inspector">Close</button>
        <h2>Inspector</h2>
        <ul className="ozira-panel-list">
          {['Layers', 'Layout', 'Styles', 'Variants', 'Prototype', 'Export'].map((item) => (
            <li key={item}><button type="button" disabled>{item}</button></li>
          ))}
        </ul>
      </aside>
    </section>
    </ProductSidebarShell>
  );
}

function ProductWorkspace({ productId, authenticated, loading, settingsRoute, onOpenSettings, onBackFromSettings, onNavigateProduct, organizationTenantId }: {
  productId: ProductId;
  authenticated: boolean;
  loading: boolean;
  settingsRoute: boolean;
  onOpenSettings: () => void;
  onBackFromSettings: () => void;
  onNavigateProduct: (productId: ProductId) => void;
  organizationTenantId: string | null;
}) {
  if (settingsRoute && authenticated) return <SettingsScreen key={productId} productId={productId} onBack={onBackFromSettings} />;
  if (productId === 'assistant') {
    return <AssistantWorkspace authenticated={authenticated} loading={loading} onOpenSettings={onOpenSettings} onNavigateProduct={onNavigateProduct} />;
  }
  if (productId === 'code') return <CodeWorkspace onNavigateProduct={onNavigateProduct} onOpenSettings={onOpenSettings} organizationTenantId={organizationTenantId} />;
  if (productId === 'design') return <DesignWorkspace authenticated={authenticated} loading={loading} onNavigateProduct={onNavigateProduct} onOpenSettings={onOpenSettings} organizationTenantId={organizationTenantId} />;
  if (productId === 'p') return <PWorkspace onNavigateProduct={onNavigateProduct} onOpenSettings={onOpenSettings} organizationTenantId={organizationTenantId} />;
  return <ForwardWorkspace onNavigateProduct={onNavigateProduct} onOpenSettings={onOpenSettings} organizationTenantId={organizationTenantId} />;
}

export default function App() {
  const { status, email, organization } = useSession();
  const { language, setLanguage, mode } = useTheme();
  const [, setLocationVersion] = useState(0);
  const productId = resolveProductId(window.location);
  const settingsRoute = isSettingsRoute(window.location);
  const meta = PRODUCT_META[productId];
  const loading = status === 'loading' || status === 'idle';
  // A cached browser session is not authentication authority. Only the
  // backend-verified Principal may open account data or the live Assistant.
  const authenticated = organization.state === 'PERSONAL' || organization.state === 'ORGANIZATION';
  const sessionResolved = useRef(false);
  if (status === 'ready' || authenticated) sessionResolved.current = true;

  // Both effects run unconditionally on every render, before any of the
  // early returns below -- React requires every render of this component to
  // call the same hooks in the same order. Signing in doesn't remount App,
  // it just flips `authenticated` on the next render, so a hook called only
  // on one side of a branch (e.g. after an early return) mismatches the
  // hook count between the signed-out and signed-in renders and crashes
  // with "Rendered more hooks than during the previous render."
  useEffect(() => {
    const onPopState = () => setLocationVersion((value) => value + 1);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    document.title = `OZIRA ${meta.label}`;
  }, [meta.label]);

  // Landing at the bare primary domain and signing in should visibly arrive
  // at /chat/, not sit on / while the Chat workspace renders underneath it --
  // same in-place pushState pattern as navigateToProduct, run once per sign-in.
  const redirectedAfterSignIn = useRef(false);
  useEffect(() => {
    if (!authenticated) { redirectedAfterSignIn.current = false; return; }
    if (redirectedAfterSignIn.current) return;
    if (!isPrimaryDomainEntry(window.location)) return;
    redirectedAfterSignIn.current = true;
    window.history.pushState({}, '', `/${PRODUCT_PATH_SEGMENT.assistant}/`);
    setLocationVersion((value) => value + 1);
  }, [authenticated]);

  // A share link is a deliberately UNAUTHENTICATED read (the bearer token IS
  // the authority -- lib/shareGrants.js's resolveShare()). Checked before
  // sign-in/product routing so a shared link never requires an account.
  if (isShareRoute(window.location)) {
    const token = shareToken(window.location);
    return token ? <SharedConversationScreen token={token} /> : <SignInScreen />;
  }

  // Restoring is not signed out. Never flash login or mount protected content
  // until the backend has verified the identity. Keep the URL untouched.
  if (!authenticated && !sessionResolved.current && (loading || status === 'error')) {
    return <WorkspaceRestoreState failed={status === 'error'} />;
  }

  // At the primary domain, sign-in is the only thing shown before
  // authentication -- no multi-product chooser. Once signed in, execution
  // falls through below and renders the Assistant workspace directly,
  // exactly like every other product entry point.
  if (isPrimaryDomainEntry(window.location) && !authenticated) return <SignInScreen />;

  const openSettings = () => {
    if (!authenticated || settingsRoute) return;
    window.history.pushState({ oziraSettings: true, from: `${window.location.pathname}${window.location.search}` }, '', settingsPath(SETTINGS_PRODUCT_SEGMENT[productId]));
    setLocationVersion((value) => value + 1);
  };

  const backFromSettings = () => {
    if (window.history.state?.oziraSettings) window.history.back();
    // A direct load of /<product>/settings (no prior in-app navigation to
    // return to) lands back on that same product's own workspace, not
    // always Chat -- resolveProductId() already resolves the current
    // productId correctly from a /<product>/settings path.
    else window.history.pushState({}, '', `/${PRODUCT_PATH_SEGMENT[productId]}/`);
    if (!window.history.state?.oziraSettings) setLocationVersion((value) => value + 1);
  };

  // Switching between Chat/Code/Design/Forward used to be a plain <a href>
  // to productUrls[id] -- a real cross-domain link on staging, where each
  // product genuinely lives on its own subdomain, but on production every
  // one of those URLs (www.ozira.et/<id>/) resolves to this exact same
  // document (server.js's isPrimaryHost routing), so that "navigation" was
  // a full page reload back into the app that was already running. Same
  // pattern as openSettings/backFromSettings above: update the URL and
  // re-render in place. Falls back to a real navigation only when the
  // target genuinely is a different origin (staging), since a browser
  // cannot switch origin without one.
  const navigateToProduct = (targetProductId: ProductId) => {
    if (targetProductId === productId) return;
    const targetUrl = getConfig().productUrls[targetProductId];
    if (!targetUrl) return;
    if (isSameOriginProductUrl(targetUrl, window.location.origin)) {
      window.history.pushState({}, '', `/${PRODUCT_PATH_SEGMENT[targetProductId]}/`);
      setLocationVersion((value) => value + 1);
      return;
    }
    window.location.href = buildProductHandoffUrl(targetUrl, {
      tenantId: organization.state === 'ORGANIZATION' ? organization.tenantId : null,
      language,
      theme: mode,
    });
  };

  return (
    <div className="ozira-product-root" data-product={productId} data-figma-node={meta.figma}>
      <OziraTrustRail
        environment={getEnvironment()}
        authState={loading ? 'loading' : authenticated ? 'authenticated' : 'unauthenticated'}
        note={authenticated ? undefined : 'Sign in to access your data.'}
      />

      <section className="ozira-product-intro" aria-labelledby="ozira-product-title">
        <p className="ozira-product-intro__eyebrow">{meta.eyebrow}</p>
        <h1 id="ozira-product-title">{meta.title}</h1>
        <p className="ozira-product-intro__description">{meta.description}</p>
      </section>

        <OziraContextBar
        organization={organization}
        projectLabel="No project selected"
        principalLabel={authenticated ? email ?? 'Account' : 'Guest'}
        language={language}
        onLanguageChange={setLanguage}
        productSwitcher={
          <OziraProductSwitcher
            currentProductId={productId}
            productUrls={getConfig().productUrls}
            context={{
              tenantId: organization.state === 'ORGANIZATION' ? organization.tenantId : null,
              language,
              theme: mode,
            }}
            onNavigate={(_url, targetProductId) => navigateToProduct(targetProductId as ProductId)}
          />
        }
        onAccountClick={authenticated ? openSettings : undefined}
        accountLabel="Account settings"
      />

      <main className="ozira-product-workspace">
        <ProductWorkspace
          productId={productId}
          authenticated={authenticated}
          loading={loading}
          settingsRoute={settingsRoute}
          onOpenSettings={openSettings}
          onBackFromSettings={backFromSettings}
          onNavigateProduct={navigateToProduct}
          organizationTenantId={organization.state === 'ORGANIZATION' ? organization.tenantId : null}
        />
      </main>
    </div>
  );
}
